import { ref, update, get, push } from 'firebase/database';
import { database } from '../firebase/firebaseConfig';
import {
  addRecurringTransactionWithChildren,
  addTransaction,
  updateTransaction,
} from '../firebase/firebaseUtils';

const generateRecurrenceDates = (startDate, endDate, interval, recurrence) => {
  if (!startDate || !endDate) {
    return [startDate];
  }

  const dates = [];
  const [sY, sM, sD] = startDate.split('-').map(Number);
  const [eY, eM, eD] = endDate.split('-').map(Number);
  if ([sY, sM, sD, eY, eM, eD].some(Number.isNaN)) {
    return [startDate];
  }

  const safeInterval = Math.max(1, parseInt(interval, 10) || 1);
  let currentDate = new Date(Date.UTC(sY, sM - 1, sD, 12));
  const end = new Date(Date.UTC(eY, eM - 1, eD, 12));

  while (currentDate <= end) {
    dates.push(currentDate.toISOString().split('T')[0]);
    if (recurrence === 'weekly') {
      currentDate.setUTCDate(currentDate.getUTCDate() + 7 * safeInterval);
    } else if (recurrence === 'monthly') {
      currentDate.setUTCMonth(currentDate.getUTCMonth() + safeInterval);
    } else if (recurrence === 'annually') {
      currentDate.setUTCFullYear(currentDate.getUTCFullYear() + safeInterval);
    } else if (recurrence === 'custom') {
      currentDate.setUTCDate(currentDate.getUTCDate() + safeInterval);
    } else {
      break;
    }
  }

  return dates;
};

export const createTransactionWithRecurrence = async (userId, transaction) => {
  if (!userId) {
    throw new Error('Cannot create transaction without a user id');
  }
  if (!transaction) {
    throw new Error('Cannot create an empty transaction');
  }

  const {
    description,
    amount,
    fromAccount,
    toAccount,
    date,
    recurrence,
    recurrenceEndDate,
    recurrenceInterval,
  } = transaction;

  const normalisedAmount = typeof amount === 'number' ? amount : Number(amount);

  if (!Number.isFinite(normalisedAmount)) {
    throw new Error('Transaction amount must be a valid number');
  }

  const baseTransaction = {
    description,
    amount: normalisedAmount,
    fromAccount,
    toAccount,
    date,
    isPaid: false,
  };

  if (!recurrence) {
    await addTransaction(userId, baseTransaction);
    return;
  }

  const dates = generateRecurrenceDates(
    date,
    recurrenceEndDate,
    recurrenceInterval,
    recurrence,
  );

  const parentTransaction = {
    ...baseTransaction,
    recurrence,
    recurrenceEndDate,
    recurrenceInterval,
  };

  const childTransactions = dates.map((transactionDate) => ({
    ...baseTransaction,
    date: transactionDate,
  }));

  await addRecurringTransactionWithChildren(
    userId,
    parentTransaction,
    childTransactions,
  );
};

export const saveTransactionEdits = async (userId, updatedTransaction) => {
  if (!userId || !updatedTransaction) return;

  const {
    id,
    parentId,
    editMode,
    date,
    recurrence,
    recurrenceEndDate,
    recurrenceInterval,
    ...transactionData
  } = updatedTransaction;
  const { isPaid, ...transactionDataWithoutPaid } = transactionData;

  if (!id) {
    console.error('Missing transaction id for save');
    return;
  }

  const mode = editMode || 'single';

  if (mode === 'single' || !parentId) {
    const singleUpdate = { ...transactionData, date };
    await updateTransaction(userId, id, singleUpdate, parentId);
    return;
  }

  if (mode === 'series' && parentId) {
    if (!recurrenceEndDate) {
      console.error('Recurrence end date is required for series update.');
      return;
    }

    const parentRef = ref(database, `users/${userId}/transactions/${parentId}`);
    await update(parentRef, {
      ...transactionDataWithoutPaid,
      date,
      recurrence,
      recurrenceEndDate,
      recurrenceInterval,
    });

    const dates = generateRecurrenceDates(date, recurrenceEndDate, recurrenceInterval, recurrence);

    const childSnapshot = await get(
      ref(database, `users/${userId}/transactions/${parentId}/childTransactions`)
    );
    const childEntries = Object.entries(childSnapshot.val() || {}).sort(
      (a, b) => new Date(a[1].date) - new Date(b[1].date)
    );
    const unpaidEntries = childEntries.filter(([_, tx]) => !tx.isPaid);

    const updates = {};
    unpaidEntries.forEach(([childId, childTx], index) => {
      if (index < dates.length) {
        updates[`users/${userId}/transactions/${parentId}/childTransactions/${childId}`] = {
          ...childTx,
          ...transactionDataWithoutPaid,
          date: dates[index],
        };
      } else {
        updates[`users/${userId}/transactions/${parentId}/childTransactions/${childId}`] = null;
      }
    });

    if (dates.length > unpaidEntries.length) {
      const childRef = ref(database, `users/${userId}/transactions/${parentId}/childTransactions`);
      for (let i = unpaidEntries.length; i < dates.length; i++) {
        const newChildRef = push(childRef);
        updates[
          `users/${userId}/transactions/${parentId}/childTransactions/${newChildRef.key}`
        ] = {
          ...transactionDataWithoutPaid,
          date: dates[i],
          isPaid: false,
          parentId,
        };
      }
    }

    await update(ref(database), updates);
  }
};
